import { FC, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";

import { copy } from "shared/assets";
import { User } from "shared/assets/icons";
import { FormBuilder, Modal } from "shared/components";
import { addTemporaryToast } from "shared/components/Toast";
import { IAPIState, IJSONAPIErrorCodes, IStatus } from "shared/types";
import { dataMetaSelector, dispatchGetVenueAction, inviteEditor } from "Venues/state";
import { IVenue, VENUE_ACTIONS } from "Venues/types";
import { useClearActionMetaOnUnMount } from "Venues/utils";

import { fields, getButtons } from "./venueFormData";

interface InviteEditorsModalProps {
  title: string;
  venue: IVenue;
  handleClose: () => void;
}

export const VenueInviteEditorsModal: FC<InviteEditorsModalProps> = ({
  title,
  venue,
  handleClose,
}) => {
  const dispatch = useDispatch();
  const { api: apiMeta } = useSelector(dataMetaSelector(VENUE_ACTIONS.inviteEditor));

  useClearActionMetaOnUnMount(VENUE_ACTIONS.inviteEditor);

  const buttons = useMemo(() => getButtons(handleClose), [handleClose]);

  const onSubmit = ({ email }: { email: string }) => {
    dispatch(inviteEditor({ venueId: venue.id, email }));
  };

  // on success show success message and close modal
  useEffect(() => {
    if (apiMeta?.status === IAPIState.succeeded) {
      dispatch(
        addTemporaryToast({
          message: copy.venueEditors.invite.success,
          status: IStatus.success,
        })
      );
      // refetch venue to update invites and users
      dispatchGetVenueAction(dispatch, venue.id);

      handleClose();
    }
  }, [apiMeta, venue.id, dispatch, handleClose]);

  const errorMessage = useMemo(() => {
    if (apiMeta?.error) {
      const { errorCodes } = apiMeta.error;

      if (errorCodes.includes(IJSONAPIErrorCodes.NotUniqueError)) {
        return copy.venueEditors.invite.errors.alreadyInvite;
      }
      if (errorCodes.includes(IJSONAPIErrorCodes.IsEditor)) {
        return copy.venueEditors.invite.errors.alreadyEditor;
      }
      return copy.errors.general;
    }
    return false;
  }, [apiMeta?.error]);

  return (
    <Modal
      handleClose={handleClose}
      title={`${copy.venueEditors.invite.title} ${title}`}
      icon={User}
    >
      <FormBuilder
        onSubmit={onSubmit}
        fields={fields}
        buttons={buttons}
        isSubmitting={apiMeta?.status === IAPIState.pending}
        apiErrorMessage={errorMessage}
      />
    </Modal>
  );
};
